import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MapPin, CheckCircle, Trash2, Eye, EyeOff, BookOpen } from 'lucide-react';
import AdminActionMenu from '@/components/AdminActionMenu';


export default function AdminCourts() {
 const navigate = useNavigate();
 const [user, setUser] = useState(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [memoryPrompt, setMemoryPrompt] = useState(null); // { courtId, courtName }
 const [sponsorInput, setSponsorInput] = useState('');
 const queryClient = useQueryClient();

 // Auto-scroll to bottom on mobile
 useEffect(() => {
   if (window.innerWidth < 768) {
     setTimeout(() => {
       window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
     }, 100);
   }
 }, []);

 useEffect(() => {
   supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
       if (!authUser) { navigate('/map'); return; }
       const { data: profile } = await supabase.from('users').select('*').eq('id', authUser.id).single();
       if (!profile || profile.role !== 'admin') {
         navigate('/map');
       } else {
         setUser({ ...authUser, ...profile });
       }
     }).catch(() => navigate('/map'));
 }, [navigate]);


 const { data: courts = [] } = useQuery({
   queryKey: ['adminCourts'],
   queryFn: async () => {
     const { data: playgrounds } = await supabase.from('playgrounds').select('*').eq('is_active', true).limit(200);
     return playgrounds || [];
   },
   enabled: !!user?.id
 });


 const filteredCourts = courts.filter(court =>
   court.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
   court.city?.toLowerCase().includes(searchTerm.toLowerCase())
 );


 const validateCourtMutation = useMutation({
   mutationFn: async (courtId) => {
     const { error } = await supabase.from('playgrounds').update({ validation_status: 'validated', validated_at: new Date().toISOString() }).eq('id', courtId);
     if (error) throw error;
     return { success: true };
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminCourts'] });
   }
 });


 const deleteMutation = useMutation({
   mutationFn: async (courtId) => {
     const { error } = await supabase.from('playgrounds').update({ is_active: false }).eq('id', courtId);
     if (error) throw error;
     return { success: true };
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminCourts'] });
   }
 });


 const hideMutation = useMutation({
   mutationFn: async (courtId) => {
     const court = courts.find(c => c.id === courtId);
     await base44.entities.Playground.update(courtId, {
       is_hidden: !court.is_hidden
     });
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminCourts'] });
   }
 });


 const memoryMutation = useMutation({
   mutationFn: async ({ courtId, sponsor }) => {
     const { data: pg } = await supabase.from('playgrounds').select('memory_enabled').eq('id', courtId).single();
     const { error } = await supabase.from('playgrounds').update({ memory_enabled: !pg?.memory_enabled }).eq('id', courtId);
     if (error) throw error;
     return { success: true };
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminCourts'] });
     setMemoryPrompt(null);
     setSponsorInput('');
   }
 });

 const cancelValidationMutation = useMutation({
   mutationFn: async (courtId) => {
     await base44.entities.Playground.update(courtId, {
       validation_status: 'unvalidated',
       validated_at: null
     });
   },
   onSuccess: () => {
     queryClient.invalidateQueries({ queryKey: ['adminCourts'] });
   }
 });


 if (!user) {
   return <div className="flex items-center justify-center min-h-screen text-white">Chargement...</div>;
 }


 return (
     <div className="bg-[#0A0B10] text-white p-6">
     <div className="max-w-6xl mx-auto">
       {/* Header */}
       <div className="flex items-center gap-4 mb-6">
         <button
           className="text-[#B3B6C2] hover:text-white"
           onClick={() => navigate('/admin')}
         >
           <ArrowLeft className="w-5 h-5" />
         </button>
         <h1 className="text-lg font-black uppercase tracking-tight">Terrains</h1>
       </div>


       {/* Search */}
       <div className="mb-6">
         <Input
           placeholder="Rechercher un terrain..."
           value={searchTerm}
           onChange={(e) => setSearchTerm(e.target.value)}
           className="bg-[#13151D] border-[#6E2BFF] text-white placeholder-[#B3B6C2]"
         />
       </div>


       {/* Courts List */}
       <div className="grid gap-4">
         {filteredCourts.length === 0 ? (
           <p className="text-[#B3B6C2] text-center py-8">Aucun terrain trouvé</p>
         ) : (
           filteredCourts.map(court => (
             <Card
               key={court.id}
               className="bg-[#13151D] border-l-4 border-l-[#6E2BFF] p-4 hover:bg-[#1B1E28] transition-all"
             >
               <div className="flex items-start justify-between gap-4">
                 <div className="flex-1 text-white">
                   <div className="flex items-center gap-2 mb-2">
                     <MapPin className="w-4 h-4 text-[#6E2BFF]" />
                     <h3 className="text-lg font-bold uppercase tracking-tight">{court.name}</h3>
                   </div>
                   <p className="text-sm text-[#B3B6C2] mb-3">
                     {[court.address, court.city, court.zip].filter(Boolean).join(', ')}
                   </p>
                  
                   <div className="grid grid-cols-2 md:grid-cols-6 gap-3 pt-2 border-t border-[#1B1E28]">
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Statut</p>
                       <p className="text-white font-bold mt-1 text-xs">{court.status}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Propriétaire</p>
                       <p className="text-white font-bold mt-1 text-xs">{court.owner_name || 'Aucun'}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Validation</p>
                       <p className="text-white font-bold mt-1 text-xs">{court.validation_status}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Matchs</p>
                       <p className="text-white font-bold mt-1">{court.validation_matches_count || 0}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Joueurs</p>
                       <p className="text-white font-bold mt-1">{court.validation_players_count || 0}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Créé le</p>
                       <p className="text-white text-xs mt-1">{new Date(court.created_date).toLocaleDateString('fr-FR')}</p>
                     </div>
                     <div>
                       <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-semibold">Mémoire</p>
                       <p className={`font-bold mt-1 text-xs ${court.memory_enabled ? 'text-amber-400' : 'text-gray-600'}`}>
                         {court.memory_enabled ? 'ON' : 'OFF'}
                       </p>
                     </div>
                   </div>
                   <div className="mt-3 pt-3 border-t border-[#1B1E28]">
                     <AdminActionMenu
                       primaryAction={
                         court.validation_status === 'unvalidated' ? {
                           label: 'Valider',
                           icon: CheckCircle,
                           onClick: () => validateCourtMutation.mutate(court.id),
                           disabled: validateCourtMutation.isPending
                         } : {
                           label: 'Annuler la validation',
                           icon: CheckCircle,
                           onClick: () => cancelValidationMutation.mutate(court.id),
                           disabled: cancelValidationMutation.isPending
                         }
                       }
                       secondaryActions={[
                         {
                           label: court.is_hidden ? 'Afficher' : 'Masquer',
                           icon: court.is_hidden ? Eye : EyeOff,
                           onClick: () => hideMutation.mutate(court.id),
                           disabled: hideMutation.isPending
                         },
                         ...(!court.memory_enabled ? [{
                           label: 'Activer mémoire',
                           icon: BookOpen,
                           onClick: () => { setMemoryPrompt({ courtId: court.id, courtName: court.name }); setSponsorInput(''); },
                           disabled: memoryMutation.isPending
                         }] : [])
                       ]}
                       dangerousActions={[
                         {
                           label: 'Supprimer',
                           icon: Trash2,
                           message: `Supprimer le terrain "${court.name}" ? Cette action est irréversible.`,
                           onConfirm: () => deleteMutation.mutate(court.id),
                           isPending: deleteMutation.isPending
                         }
                       ]}
                     />
                   </div>
                 </div>
               </div>
             </Card>
           ))
         )}
       </div>

       {/* Memory Activation Prompt */}
       {memoryPrompt && (
         <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
           <div className="bg-[#13151D] rounded-xl p-6 max-w-sm w-full border border-amber-500/30 space-y-4">
             <div className="flex items-center gap-2">
               <BookOpen className="w-5 h-5 text-amber-500" />
               <h3 className="text-white font-bold">Activer la mémoire</h3>
             </div>
             <p className="text-sm text-[#B3B6C2]">
               Terrain : <span className="text-white font-semibold">{memoryPrompt.courtName}</span>
             </p>
             <p className="text-xs text-amber-500/80">
               Cette action est irréversible. La mémoire ne pourra plus être désactivée.
             </p>
             <div>
               <label className="text-xs text-[#B3B6C2] uppercase tracking-wider font-semibold mb-1 block">
                 Sponsor (obligatoire)
               </label>
               <Input
                 placeholder="Nom du sponsor..."
                 value={sponsorInput}
                 onChange={(e) => setSponsorInput(e.target.value)}
                 className="bg-[#0A0B10] border-amber-500/30 text-white placeholder-[#B3B6C2]"
               />
             </div>
             <div className="flex gap-2">
               <Button
                 variant="ghost"
                 className="flex-1 text-[#B3B6C2] hover:text-white"
                 onClick={() => { setMemoryPrompt(null); setSponsorInput(''); }}
               >
                 Annuler
               </Button>
               <Button
                 className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold"
                 disabled={!sponsorInput.trim() || memoryMutation.isPending}
                 onClick={() => memoryMutation.mutate({ courtId: memoryPrompt.courtId, sponsor: sponsorInput.trim() })}
               >
                 {memoryMutation.isPending ? 'Activation...' : 'Activer'}
               </Button>
             </div>
           </div>
         </div>
       )}
     </div>
   </div>
 );
}